<template>
    <div class="coverage-map">
        <div class="coverage-map__panel">
            <div class="coverage-map__panel__header">{{ $t('detail.map') }}</div>
            <div class="coverage-map__panel__content">
                <transition name="fade">
                    <div v-if="loading" class="loading-wrapper">
                        <BaseSpinner text="info.loading_data" />
                    </div>
                </transition>
                <MglMap
                        class="coverage-map__panel__content__canvas"
                        :trackResize="true"
                        :attributionControl="false"
                        :accessToken="accessToken"
                        :mapStyle="mapStyle"
                        :center="this.coordinates"
                        :maxZoom="maxZoom"
                        :minZoom="minZoom"
                        :zoom="zoom"
                        @load="mapLoaded">
                    <MglFullscreenControl position="top-right"></MglFullscreenControl>
                    <MglNavigationControl position="top-right"></MglNavigationControl>
                </MglMap>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import 'mapbox-gl/dist/mapbox-gl.css';
    import 'vue-mapbox/dist/vue-mapbox.css';
    import MglGeocoderControl from '@mapbox/mapbox-gl-geocoder';
    import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
    import Mapbox from 'mapbox-gl';
    import {
        MglMap,
        MglMarker,
        MglPopup,
        MglNavigationControl,
        MglFullscreenControl
    } from 'vue-mapbox';

    export default {
        name: 'CoverageMap',
        props: {
            unit: {
                type: Object,
            }
        },
        components: {
            MglMap,
            MglMarker,
            MglPopup,
            MglNavigationControl,
            MglFullscreenControl
        },
        data() {
            return {
                loading: false,
                maxZoom: 20,
                minZoom: 1,
                zoom: 4,
                accessToken: process.env.VUE_APP_MAPBOX_KEY,
                mapStyle: 'mapbox://styles/mapbox/streets-v11' // your map style
            };
        },
        computed: {
            ...mapGetters('tentacle', ['sigfoxCoverage']),
            coordinates() {
                return [4.455399532609533, 51.924542532352774];
            }
        },
        created() {
            // We need to set mapbox-gl library here in order to use it in template
            this.mapbox = Mapbox;
        },
        methods: {
            mapLoaded(e) {
                // in component https://docs.mapbox.com/mapbox-gl-js/example/point-from-geocoder-result/
                this.map = e.map;

                let manualMarker = new Mapbox.Marker({
                    color: '#da444490'
                });

                const self = this;
                this.map.on('click', function (e) {
                    let lat = e.lngLat.lat;
                    let lng = e.lngLat.lng;

                    manualMarker.setLngLat([lng, lat])
                        .addTo(this);

                    self.checkCoverage(lng, lat);
                });

                var geocoder = new MglGeocoderControl({
                    accessToken: this.accessToken,
                    limit: 3,
                    marker: false,
                    mapboxgl: Mapbox,
                });

                this.map.addControl(geocoder);
            },
            async checkCoverage(lng, lat) {
                this.loading = true;
                await this.$store.dispatch('tentacle/checkSigfoxCoverage', {lng: lng, lat: lat});

                let redundancy = this.$store.getters['tentacle/sigfoxCoverage'];
                let result = this.getMarginPopupResults(redundancy);
                new Mapbox.Popup({offset: 25, closeButton: false})
                    .setLngLat([lng, lat])
                    .setHTML('<div class="map-popup"><h4>' + result + '</h4></div>')
                    .addTo(this.map);

                this.loading = false;
            },
            getMarginPopupResults(redundancy) {
                if (redundancy === null) {
                    return '<p class="coverage-none">' + this.$t('sigfox_coverage.coverage_error') + '</p>';
                }

                let result = this.$t('sigfox_coverage.unstable');
                let coloringClass = 'coverage-none';

                /*
                if (redundancy === 1) {
                    result = this.$t('sigfox_coverage.works_open');
                    coloringClass = 'coverage-working';
                }
                if (redundancy === 2) {
                    result = this.$t('sigfox_coverage.possible_indoor');
                    coloringClass = 'coverage-average';
                }
                if (margin1 > 30 || margin2 > 30 || margin3 > 30) {
                    result = this.$t('sigfox_coverage.possible_underground');
                    coloringClass = 'coverage-good';
                }
                */

                if (redundancy === 1) {
                    result = this.$t('sigfox_coverage.possible_open');
                    coloringClass = 'coverage-bad';
                }
                if (redundancy === 2) {
                    result = this.$t('sigfox_coverage.works_indoor');
                    coloringClass = 'coverage-sufficient';
                }
                if (redundancy === 3) {
                    result = this.$t('sigfox_coverage.works_underground');
                    coloringClass = 'coverage-best';
                }

                return '<div class="coverage-info"><svg class="coverage-icon icon" width="24" height="24"><use href="/svg-sprite.svg#'+ coloringClass +'"></use></svg><p class="coverage-message ' + coloringClass + '">' + result + '</p>';
            }
        },
    };
</script>
<style>
    .coverage-icon {
        margin: auto;
        min-width: 45px;
        height: 36px;
    }

    .coverage-message {
        margin-left: 15px;
    }

    .coverage-info {
        display: flex;
    }

    canvas.mapboxgl-canvas {
        outline: none;
        border: 0.5px solid rgba(172, 180, 197, 0.31);
        border-top: none;
        border-radius: 0 0 2rem 2rem;
    }

    .mapboxgl-popup-content {
        padding: 0 20px;
        width: 350px;
    }

    .map-popup > h3 {
        margin-top: -1px;
        margin-bottom: 2px;
    }

    .coverage-best {
        color: #00c05c;
    }

    .coverage-good {
        color: #6fc908;
    }

    .coverage-sufficient {
        color: #aed11e;
    }

    .coverage-average {
        color: #d8d004;
    }

    .coverage-working {
        color: #d59807;
    }

    .coverage-bad {
        color: #cb6e00;
    }

    .coverage-none {
        color: #d40400;
    }
</style>

<style lang="scss" scoped>
    .coverage-map {
        padding-left: 8rem;
        @media only screen and (max-width: 1199px) {
            padding-left: 0;
            width: 90%;
            margin-right: auto;
            margin-left: 5%;
        }

        &__panel {
            margin-bottom: 4rem;
            border-radius: 2rem;
            background-color: #fff;
            box-shadow: 0 0 5px rgba(100, 116, 160, 0.1);
            position: relative;

            .loading-wrapper {
                position: absolute;
                top: 6rem;
                width: 100%;
                height: calc(100% - 6rem);
                background-color: rgba(255, 255, 255, 0.8);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 2;
            }

            &__header {
                padding: 0 3rem;
                height: 6rem;
                border-bottom: 0.1rem solid #eee;
                display: flex;
                align-items: center;
                font-weight: 600;
                font-size: 1.4rem;
                color: $grey;
            }

            &__content {
                min-height: 42rem;
                @media only screen and (max-width: 1199px) {
                    min-height: 26rem;
                }

                &__canvas {
                    height: 42rem;
                    border-radius: 0 0 2rem 2rem;
                    overflow: hidden;
                }
            }
        }
    }
</style>
